// lib
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { List, Map } from 'immutable'
import { ERROR_TYPES } from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/decorators/ErrorsDecoratorFactory'
import errorAnchorText from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/decorators/error-anchor-text'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'
import UpgradePackageMark from '@aytm/app/survey/editor/components/lib/UpgradePackageMark'

// styles
import style from '@aytm/lib/rfd-ui/styles/advanced/draft-js-decorators.module.scss'
import errorPopupStyle from '@aytm/app/survey/editor/styles/lib/ErrorPopup.module.scss'

// constants
const TRANSLATION_TEXT_ERROR_TYPES = [
  'overlimit',
  'overlimit_skipper',
  'outdated',
  'outdated_skipper',
  'outdated_no_translate'
]
const templateVariablesRegex = /{([A-Z_a-z]*)}/g

// <Component>
ErrorsDecoratorPopup.propTypes = {
  locale: PropTypes.instanceOf(Map).isRequired,
  errors: PropTypes.instanceOf(List),
  textProps: PropTypes.any,
  errorTypes: PropTypes.object,
  updateOldRefs: PropTypes.func
}
export default React.memo(
  ErrorsDecoratorPopup,
  (prevProps, nextProps) =>
    prevProps.locale === nextProps.locale &&
    prevProps.errors === nextProps.errors &&
    prevProps.textProps === nextProps.textProps
)
function ErrorsDecoratorPopup(props) {
  const { locale, errors, errorTypes = ERROR_TYPES, textProps, updateOldRefs } = props

  const [showPopup, setShowPopup] = useState(false)

  const errorText = textProps.children[0].props.text
  const errorObj = extractErrorObject(errors, errorText)
  const errorType = errorObj ? errorObj.get('type') : 'parse'
  const errorTypeText = extractErrorTypeText(locale, errorObj, errorType, errorTypes)

  return errorTypes[errorType] ? (
    <span
      className={style.popupTrigger}
      onMouseEnter={() => void setShowPopup(true)}
      onMouseLeave={() => void setShowPopup(false)}
    >
      <Popup
        className={`${errorPopupStyle.component} ${errorPopupStyle.topleft}`}
        direction="topleft"
        renderRef={errorText}
        portal={true}
        width="330px"
        show={Boolean(showPopup)}
        // show={true}
        content={() => (
          <div className={style.content}>
            <div>
              {'aytm_logic_required' === errorType ? (
                <span className={style.popupContent}>{errorTypeText}</span>
              ) : TRANSLATION_TEXT_ERROR_TYPES.some(type => errorsIncludesType(errors, type)) ? null : (
                <span className={style.popupContent} dangerouslySetInnerHTML={{ __html: errorTypeText }} />
              )}
              {'old' === errorType && (
                <span>
                  <br />
                  <br />
                  <a onClick={() => updateOldRefs(errorObj)}>{locale.get('okHere')}</a>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <a onClick={() => updateOldRefs(errorObj, true)}>{locale.get('okHereAndElsewhere')}</a>
                </span>
              )}
              {renderTranslationErrors({ locale, errors })}
            </div>
            <div className={style.popupArrowContainer}>
              <div className={style.popupArrow} />
            </div>
          </div>
        )}
      />
      <span className={errorTypes[errorType].style}>{textProps.children}</span>
    </span>
  ) : (
    textProps.children
  )
}

function errorsIncludesType(errors, errorType) {
  return errors.some(error => error.get('type') === errorType)
}
function findError(errors, { type }) {
  return errors.find(error => error.get('type') === type) || null
}

function extractErrorObject(errors, errorText) {
  return errors.find(error => {
    const text = errorAnchorText(error)
    const type = error.get('type')
    if (!text) {
      return false
    }
    if (TRANSLATION_TEXT_ERROR_TYPES.includes(type)) {
      return true
    }

    const errorTextCI = errorText.toUpperCase()
    const errorTextTrimmed = errorTextCI.trim()
    const normalizedText = text.replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&amp;/gi, '&').toUpperCase()
    const normalizedTextTrimmed = normalizedText.trim()

    if (normalizedTextTrimmed === errorTextTrimmed) {
      return true
    }

    return 'parse' === type
      ? '[' + normalizedText + ']' === errorText.toUpperCase()
      : 'cluster' === type
        ? '"' + normalizedText + '"' === errorText.toUpperCase() ||
          "'" + normalizedText + "'" === errorText.toUpperCase()
        : false
  })
}
function extractErrorHint(errorObj, errorKey) {
  return errorObj
    ? 0 <= ['index', 'rule'].indexOf(errorKey)
      ? `${errorObj.get('text')}${errorObj.get('subText') ? ` (${errorObj.get('subText')})` : ''}`
      : errorObj.get(errorKey)
    : ''
}
function extractErrorTypeText(locale, errorObj, errorType, errorTypes = ERROR_TYPES) {
  if (TRANSLATION_TEXT_ERROR_TYPES.includes(errorType)) {
    return null
  }
  const errorString =
    (errorObj && errorObj.get('desc')) || locale.get(errorTypes[errorType].text) || locale.get('notValidRule')

  if ('aytm_logic_required' === errorType) {
    const upgradePackage = errorObj?.get('upgradePackage') || Map()
    const packageName = upgradePackage.get('displayName', '')
    const packageURL = upgradePackage.get('upgradeURL', '')
    return (
      <div className={style.errorText}>
        {upgradePackage.get('icon') && (
          <a href={packageURL} title={packageName} target="_blank" rel="noreferrer">
            <UpgradePackageMark upgradePackage={upgradePackage} />
          </a>
        )}
        <span
          dangerouslySetInnerHTML={{
            __html: errorString.replace('{packageName}', packageName).replace('{upgradeURL}', packageURL)
          }}
        />
      </div>
    )
  } else {
    const replacements = {}
    let matchArr
    while (null !== (matchArr = templateVariablesRegex.exec(errorString))) {
      replacements[matchArr[0]] = extractErrorHint(errorObj, matchArr[1])
    }
    return Object.keys(replacements)
      .reduce((str, key) => str.replace(new RegExp(key, 'g'), replacements[key].toUpperCase()), errorString)
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
  }
}

function renderTranslationErrors({ locale, errors }) {
  const outdatedError = findError(errors, { type: 'outdated' })
  const overlimitError = findError(errors, { type: 'overlimit' })
  const outdatedSkipperError = findError(errors, { type: 'outdated_skipper' })
  const overlimitSkipperError = findError(errors, { type: 'overlimit_skipper' })
  const outdatedNoTranslateError = findError(errors, { type: 'outdated_no_translate' })

  return (
    <>
      {outdatedError && (
        <span className={style.errorContainer}>
          <div dangerouslySetInnerHTML={{ __html: locale.get('outdated') }} />
          <span className={style.actionContainer}>
            <a
              href="#"
              className={style.actionLink}
              onClick={event => {
                event?.preventDefault?.()
                event?.stopPropagation?.()
                const handler = outdatedError.get('handler')
                if ('function' === typeof handler) {
                  handler({ okHere: true })
                }
              }}
            >
              {locale.get('okHere', '*updateThis*')}
            </a>
            <span className={style.linkSeparator} />
            <a
              href="#"
              className={style.actionLink}
              onClick={event => {
                event?.preventDefault?.()
                event?.stopPropagation?.()
                const handler = outdatedError.get('handler')
                if ('function' === typeof handler) {
                  handler({ applyToAll: true })
                }
              }}
            >
              {locale.get('okHereAndOtherLanguages')}
            </a>
          </span>
        </span>
      )}
      {outdatedSkipperError && (
        <span className={style.errorContainer}>
          <span dangerouslySetInnerHTML={{ __html: locale.get('outdatedSkipper') }} />
          <span className={style.actionContainer}>
            <a
              href="#"
              className={style.actionLink}
              onClick={event => {
                event?.preventDefault?.()
                event?.stopPropagation?.()
                const handler = outdatedSkipperError.get('handler')
                if ('function' === typeof handler) {
                  handler({ translate: true })
                }
              }}
            >
              <i className={`icon-aytmfd-skipper ${style.skipperIcon}`} />
              {locale.get('outdatedSkipperButton')}
            </a>
            <span className={style.linkSeparator} />
            <a
              href="#"
              className={style.actionLink}
              onClick={event => {
                event?.preventDefault?.()
                event?.stopPropagation?.()
                const handler = outdatedSkipperError.get('handler')
                if ('function' === typeof handler) {
                  handler({ okHere: true })
                }
              }}
            >
              {locale.get('okHere', '*updateThis*')}
            </a>
          </span>
        </span>
      )}
      {outdatedNoTranslateError && (
        <span className={style.errorContainer}>
          <span dangerouslySetInnerHTML={{ __html: locale.get('outdatedSkipper') }} />
          <span className={style.actionContainer}>
            <a
              href="#"
              className={style.actionLink}
              onClick={event => {
                event?.preventDefault?.()
                event?.stopPropagation?.()
                const handler = outdatedNoTranslateError.get('handler')
                if ('function' === typeof handler) {
                  handler({ okHere: true })
                }
              }}
            >
              {locale.get('okHere', '*updateThis*')}
            </a>
          </span>
        </span>
      )}
      {overlimitError && (
        <div className={style.errorContainer}>
          <span dangerouslySetInnerHTML={{ __html: locale.get('overlimit') }} />
          <span className={style.actionContainer}></span>
        </div>
      )}
      {overlimitSkipperError && (
        <div className={style.errorContainer}>
          <span dangerouslySetInnerHTML={{ __html: locale.get('overlimitSkipper') }} />
          <span className={style.actionContainer}>
            {'function' === typeof overlimitSkipperError.get('handler') && (
              <a
                href="#"
                className={style.actionLink}
                onClick={event => {
                  event?.preventDefault?.()
                  event?.stopPropagation?.()
                  const handler = overlimitSkipperError.get('handler')
                  handler({ overlimit: true })
                }}
              >
                <i className={`icon-aytmfd-skipper ${style.skipperIcon}`} />
                {locale.get('overlimitSkipperButton')}
              </a>
            )}
          </span>
        </div>
      )}
    </>
  )
}
